import * as Yup from "yup";

import { toast } from "react-toastify";

import {
  fetchPlanList,
  fetchBrandList,
  saveDmiDetails,
  sendPaymentRequest,
} from "../../actions/dmiActions";

import {
  DEFAULT_PLAN_VARIANT_ID,
  DEFAULT_PLAN_AMOUNT,
  COUNTRY,
} from "../../constants";
import { useEffect } from "react";

// Fetch the id from the URL — used across all APIs

const searchParams = new URLSearchParams(window.location.search);

const userIdFromUrl = searchParams.get("id");

export const protectFormInitialValues = {
  name: "",

  email: "",

  mobile: "",

  imeiNumber: "",

  brandName: "",

  modelName: "",

  zipCode: "",

  address: "",

  city: "",

  state: "",

  uploadFile: null,

  uploadFileExtension: "",

  termsAgreed: false,
};

export const validationSchema = Yup.object({
  name: Yup.string().required("Name is required"),

  email: Yup.string()

    .email("Invalid email format")

    .required("Email is required"),

  mobile: Yup.string()

    .matches(/^[0-9]{10}$/, "Mobile number must be exactly 10 digits")

    .required("Mobile number is required"),

  imeiNumber: Yup.string()

    .matches(/^[0-9]{15}$/, "Invalid IMEI number")

    .required("IMEI number is required"),

  brandName: Yup.string().required("Brand Name is required"),

  modelName: Yup.string().required("Model Name is required"),

  zipCode: Yup.string()

    .matches(/^[0-9]{6}$/, "Postal Code must be exactly 6 digits")

    .required("Postal Code is required"),

  address: Yup.string().required("Address is required"),

  city: Yup.string().required("City is required"),

  state: Yup.string().required("State is required"),

  uploadFile: Yup.mixed()

    .required("Document is required")

    .test("fileSize", "File size must be under 5MB", (value) =>
      value ? value.size <= 5 * 1024 * 1024 : true,
    )

    .test("fileType", "Only JPG, JPEG, PNG or PDF allowed", (value) =>
      value
        ? ["image/jpeg", "image/png", "application/pdf"].includes(value.type)
        : true,
    ),

  termsAgreed: Yup.boolean()

    .oneOf([true], "You must agree to the Terms & Conditions")

    .required("You must agree to the Terms & Conditions"),
});

export const defaultViewPlanDetails = {
  name: "Protect",

  duration: "1 Year",

  price: "699",

  coverage: 5000,
};

export const convertToBase64 = (blob) =>
  new Promise((resolve) => {
    const reader = new FileReader();

    reader.onloadend = () => resolve(reader.result.split(",")[1]);

    reader.readAsDataURL(blob);
  });

export const getPlanList = async (setPlanDetails, setLoading) => {
  setLoading(true);

  try {
    const res = await fetchPlanList();

    if (res?.data?.statusCode === 200) setPlanDetails(res.data.data);
    else toast.error(res?.data?.message);
  } catch {
    // silent
  } finally {
    setLoading(false);
  }
};

export const getBrandList = async (setBrandList, setLoading) => {
  setLoading(true);

  try {
    const res = await fetchBrandList();

    if (res?.data?.statusCode === 200) setBrandList(res.data.data);
    else toast.error(res?.data?.message);
  } catch {
    // silent
  } finally {
    setLoading(false);
  }
};

export const openPaymentGateway = async (
  dmiDetails,
  videoRecInBase64,
  setLoading,
  setPaymentUrl,
) => {
  if (!videoRecInBase64) {
    toast.error("Please select image to upload.");
    return;
  }

  setLoading(true);

  try {
    const res = await sendPaymentRequest({
      ...dmiDetails,
      UserId: dmiDetails.UserId,
    });

    if (res?.data?.message) {
      const paymentUrl = res.data.message;

      // Save to storage for PaymentLoading page
      sessionStorage.setItem("paymentImei", dmiDetails.IMEI);
      localStorage.setItem("paymentImei", dmiDetails.IMEI);
      sessionStorage.setItem("userIdbyClient", userIdFromUrl);
      localStorage.setItem("userIdbyClient", userIdFromUrl);
      sessionStorage.setItem(
        "paymentPlanName",
        dmiDetails.plan_Description || "",
      );
      localStorage.setItem(
        "paymentPlanName",
        dmiDetails.plan_Description || "",
      );

      // console.log("Payment URL received:", paymentUrl);
      window.open(paymentUrl, "_blank", "noreferrer");
      // Triggers useEffect in ProtectForm which does: document.location = paymentUrl
      // setPaymentUrl(paymentUrl);
      // setLoading(true);
    }

    if (res?.data?.statusCode === 400 || res?.data?.statusCode === 404) {
      toast.error(res.data.message);
    }
  } catch {
    // silent
  } finally {
    setLoading(false);
  }
};

export const submitProtectForm = async (
  values,

  { selectedPlanVariantId, selectedPlanAmount, selectedPlan, videoRecInBase64 },
  setLoading,
  openPaymentGatewayFn,
) => {
  if (!userIdFromUrl) {
    toast.error(
      "User ID not found in URL. Please use a valid link to access this form.",
    );
    return;
  }

  if (!videoRecInBase64) {
    toast.error("Please select image to upload.");

    return;
  }

  setLoading(true);

  let uploadFileBase64 = "";

  if (values.uploadFile) {
    uploadFileBase64 = await convertToBase64(values.uploadFile);
  }

  const dmiDetails = {
    ClientUserId: userIdFromUrl,
    Name: values.name,
    Email: values.email,
    MobileNumber: values.mobile,
    IMEI: values.imeiNumber,
    Brand: values.brandName,
    Model: values.modelName,
    PlanVarriantId: selectedPlanVariantId || DEFAULT_PLAN_VARIANT_ID,
    plan_Description: selectedPlan || "",
    MobileImg: videoRecInBase64,
    Pincode: values.zipCode,
    Address: values.address,
    City: values.city,
    State: values.state,
    Country: COUNTRY,
    amount: selectedPlanAmount || DEFAULT_PLAN_AMOUNT,
    UploadDocument: uploadFileBase64,
    Extension: values.uploadFileExtension,
    redirectKey: "gcop",
  };

  try {
    const res = await saveDmiDetails(dmiDetails);

    if (res?.data?.statusCode === 200) {
      toast.success(res.data.message);
      const customerId = res.data.customerid; // customerid from saveDmiDetails response
      sessionStorage.setItem("userId", customerId);
      localStorage.setItem("userId", customerId);
      await openPaymentGatewayFn(
        { ...dmiDetails, UserId: customerId },
        customerId,
      );
    } else {
      toast.error(res?.data?.message);
    }
  } catch {
    // silent
  } finally {
    setLoading(false);
  }
};
